import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  DexContextProvider,
  SwapContextProvider,
  TokenContextProvider,
  TokenListContextProvider,
} from "@cyclos-io/swap-ui";
import { WalletKitProvider } from "@gokiprotocol/walletkit";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Hidden,
  IconButton,
  ThemeProvider,
} from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import { CloseRounded } from "@material-ui/icons";
import { Swap as SwapClient } from "@project-serum/swap";
import {
  TokenListContainer,
  TokenListProvider,
} from "@solana/spl-token-registry";
import { WalletAdapter } from "@solana/wallet-adapter-base";
import { PublicKey } from "@solana/web3.js";
import { SnackbarProvider, useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import CacheBuster from "./cacheBuster";
import BottomPageNavigation from "./components/BottomPageNavigation";
import NavBar from "./components/NavBar";
import { opts, REFERRAL_ACCOUNT } from "./constants";
import { StakingProvider } from "./contexts/StakingContext";
import { WalletProvider } from "./contexts/WalletContext";
import useWallet from "./hooks/useWallet";
import { NewPosition, PoolsOverview, PositionDetailed } from "./pages/pools";
import ClosedPage from "./pages/Closed";
import StakingPage from "./pages/staking";
import SwapPage from "./pages/SwapPage";
import NotifyingProvider from "./utils/NotifyingProvider";

const httpLink = new HttpLink({
  uri: "http://cyclos.me/v1/graphql", // use https for secure endpoint
});
// Create a WebSocket link:
const wsLink = new WebSocketLink({
  uri: "wss://cyclos.me/v1/graphql", // use wss for a secure endpoint
  options: {
    reconnect: true,
  },
});

// using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent
// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

// Instantiate client
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

const darkTheme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        border: "1px solid #4f538599",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
      },
    },
  },
  palette: {
    type: "dark",
    background: {
      default: "#1D0C30",
      paper: "#2C2F42e8",
      // paper: "#00032e85",
    },
    primary: {
      light: "#87dcc7",
      main: "#69D4BA",
      dark: "#499482",
      contrastText: "#000",
    },
    secondary: {
      light: "#9699c7",
      main: "#7C80B9",
      dark: "#565981",
      contrastText: "#fff",
    },
    common: {
      black: "#21252C",
    },
  },
});

function SnackbarApp() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [tokenList, setTokenList] = useState<TokenListContainer | null>(null);

  const { wallet, connection } = useWallet();

  const swapNotificationProvider = useMemo(() => {
    const provider = new NotifyingProvider(
      connection,
      wallet as WalletAdapter,
      opts,
      (tx, confirmed, err) => {
        if (err) {
          enqueueSnackbar(err.toString(), {
            variant: "error",
          });
        } else if (confirmed) {
          enqueueSnackbar("Transaction confirmed", {
            variant: "success",
            persist: true,
            action: (key) => (
              <>
                <Button
                  color="inherit"
                  component="a"
                  target="_blank"
                  rel="noopener"
                  href={`https://solscan.io/tx/${tx}`}
                >
                  View on Explorer
                </Button>
                <IconButton size="small">
                  <CloseRounded onClick={() => closeSnackbar(key)} />
                </IconButton>
              </>
            ),
          });
        } else {
          enqueueSnackbar("Transaction sent", {
            variant: "info",
            action: (
              <Button
                color="inherit"
                component="a"
                target="_blank"
                rel="noopener"
                href={`https://solscan.io/tx/${tx}`}
              >
                View on Explorer
              </Button>
            ),
          });
        }
      }
    );
    return provider;
  }, [wallet, connection, enqueueSnackbar]);

  useEffect(() => {
    new TokenListProvider().resolve().then(setTokenList);
  }, [setTokenList]);

  const commonBases: PublicKey[] = [
    new PublicKey("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"), // USDC
    // new PublicKey("Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"), // USDT
    // Arbitrary mint respresenting native SOL, as used by swap-ui
    new PublicKey("Ejmc1UB4EsES5oAaRN63SpoxMJidt3ZGBrqrZk49vjTZ"), // SOL
    // new PublicKey("SRMuApVNdxXokk5GT7XD5cUUgXMBCoAz2LHeuAoKWRt"), // SRM
    new PublicKey("BRLsMczKuaR5w9vSubF4j8HwEGGprVAyyVgS4EX7DKEg"), // CYS
  ];

  return (
    <>
      {tokenList && (
        <TokenContextProvider provider={swapNotificationProvider}>
          <TokenListContextProvider
            tokenList={tokenList as any}
            provider={swapNotificationProvider}
            commonBases={commonBases}
          >
            <DexContextProvider
              swapClient={new SwapClient(swapNotificationProvider, tokenList)}
            >
              <SwapContextProviderWrapper />
            </DexContextProvider>
          </TokenListContextProvider>
        </TokenContextProvider>
      )}
    </>
  );
}

function SwapContextProviderWrapper() {
  return (
    <SwapContextProvider referral={REFERRAL_ACCOUNT}>
      <Router>
        <Box display="flex" flexDirection="column" minHeight="100vh">
          <NavBar />
          <Container maxWidth="md" style={{ margin: "auto" }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={6}
            >
              <Switch>
                <Route exact path="/" component={SwapPage} />
                <Route exact path="/staking">
                  <StakingProvider>
                    <StakingPage />
                  </StakingProvider>
                </Route>
                <Route exact path="/pools" component={ClosedPage} />
                {/* <Route exact path="/pools" component={PoolsOverview} />
                <Route exact path="/pools/new" component={NewPosition} />
                <Route exact path="/pools/:mint" component={PositionDetailed} /> */}
                <Route>
                  <Redirect to="/" />
                </Route>
              </Switch>
            </Box>
          </Container>
          <Hidden mdUp>
            <BottomPageNavigation />
          </Hidden>
        </Box>
      </Router>
    </SwapContextProvider>
  );
}

function App() {
  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) {
          refreshCacheAndReload();
        }

        return (
          <ThemeProvider theme={darkTheme}>
            <SnackbarProvider maxSnack={5} autoHideDuration={3000}>
              <WalletKitProvider
                app={{ name: "cyclos-swap" }}
                networkConfigs={{
                  "mainnet-beta": {
                    name: "Custom RPC",
                    endpoint:
                      "https://dawn-red-log.solana-mainnet.quiknode.pro/ff88020a7deb8e7d855ad7c5125f489ef1e9db71/",
                  },
                }}
                commitment="processed"
                onConnect={() => { }}
                onDisconnect={() => { }}
                onError={() => { }}
              >
                <WalletProvider>
                  <StakingProvider>
                    <ApolloProvider client={client}>
                      <CssBaseline />
                      <SnackbarApp />
                    </ApolloProvider>
                  </StakingProvider>
                </WalletProvider>
              </WalletKitProvider>
            </SnackbarProvider>
          </ThemeProvider>
        );
      }}
    </CacheBuster>
  );
}

export default App;
